import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthStore } from '../auth.store';

@Injectable({
  providedIn: 'root',
})
class PermissionsService {
  private authStore = inject(AuthStore);

  constructor(
    private router: Router,
    private jwtService: JwtHelperService,
    private snackBar: MatSnackBar
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const token = localStorage.getItem('restful-token') ?? undefined;
    if (!token || this.jwtService.isTokenExpired(token)) {
      const message = token ? 'Session expired. Please log in again.' : 'Please log in to continue.';
      this.snackBar.open(message, 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
      this.router.navigate(['/auth/login']);
      return false;
    }

    const expectedRole = route.data['expectedRole'];
    const expectedRoles = route.data['expectedRoles'] as string[] | undefined;
    const userRole = this.authStore.role();
    let tokenRole: unknown = undefined;
    try {
      if (token) {
        const decoded: any = this.jwtService.decodeToken(token);
        tokenRole = decoded?.role ?? decoded?.user?.role;
      }
    } catch (error) {
      console.log('Error decoding token:', error);
    }

    const resolvedRole = tokenRole ?? userRole;

    if (expectedRoles?.length && !expectedRoles.includes(String(resolvedRole))) {
      this.snackBar.open('You do not have permission to access this page', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
      this.router.navigate(['/auth/login']);
      return false;
    }

    if (expectedRole && resolvedRole !== expectedRole) {
      this.snackBar.open('You do not have permission to access this page', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
      this.router.navigate(['/auth/login']);
      return false;
    }

    return true;
  }
}

export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
  return <boolean>inject(PermissionsService).canActivate(next, state);
};
